export const COMPARISON_ROWS = [
  {
    dimension: "User experience",
    standalone: "Brokers work in a dedicated O.N.E.Tech operational intelligence portal — prioritised queue, client decision profiles, governance centre and audit log in one focused workspace.",
    embedded: "The same intelligence appears as native cards, worklists and approval prompts inside the partner CRM screens the broker's teams already use.",
  },
  {
    dimension: "Branding ownership",
    standalone: "Full O.N.E.Tech branding and product identity, presented to the broker as a distinct solution.",
    embedded: "Fully partner-branded: partner name, logo and primary colour. O.N.E.Tech operates as the invisible technology layer behind the interface.",
  },
  {
    dimension: "Client relationship",
    standalone: "The broker contracts with and is onboarded by O.N.E.Tech directly.",
    embedded: "The partner owns the broker relationship end-to-end; O.N.E.Tech has no direct commercial contact with the broker.",
  },
  {
    dimension: "Deployment approach",
    standalone: "Standalone secure portal plus API/webhook connectors into the broker's CRM, KYC, payment, trading and communication systems.",
    embedded: "An enableable module inside the partner CRM, shipped within the partner's release train and activated per broker tenant.",
  },
  {
    dimension: "Data flow",
    standalone: "Signals are read from broker systems; decisions, tasks and outcomes are written back through the same APIs — with a full audit trail.",
    embedded: "Identical pipeline; the read/write operations are surfaced through the partner CRM's own data model and objects.",
  },
  {
    dimension: "Support responsibilities",
    standalone: "O.N.E.Tech supports the intelligence layer; the broker supports its own CRM and operational systems.",
    embedded: "Partner provides tier-1 support within the CRM; O.N.E.Tech supports the intelligence engine at tier-2 under a partner support agreement.",
  },
  {
    dimension: "Broker activation model",
    standalone: "Per-broker onboarding: connect systems, configure jurisdictions, policies and approval thresholds, then go live.",
    embedded: "Per-broker activation as a CRM module toggle, inheriting the partner's existing tenant configuration with per-broker policy setup.",
  },
  {
    dimension: "Suitable commercial structure",
    standalone: "Direct licence or subscription structures between O.N.E.Tech and the broker.",
    embedded: "OEM / revenue-share style structures between O.N.E.Tech and the partner, with the partner defining broker-facing packaging. Commercial terms are discussed in evaluation, not published here.",
  },
];

export const ARCH_STAGES = [
  {
    n: 1, title: "Source Systems",
    desc: "Broker CRM, KYC provider, payment system, trading platforms (MT4 / MT5 / cTrader) and communication channels emit operational events.",
    chips: ["Broker CRM", "KYC", "Payments", "MT4 / MT5 / cTrader", "Email · SMS · WhatsApp · VoIP"],
  },
  { n: 2, title: "API / Webhook Integration Layer", desc: "Inbound webhooks and scheduled API pulls capture events; outbound calls execute approved actions. Every connector is authenticated, rate-limited and logged." },
  { n: 3, title: "O.N.E.Tech Event Normalisation", desc: "Heterogeneous events are normalised into a single client-event schema with source, timestamp, jurisdiction and consent metadata." },
  { n: 4, title: "Client State Classification", desc: "Each client is placed on the lifecycle (Lead → FTD → KYC → Funded → Active Trader → Retention Risk → Dormant → Withdrawal) with blockage, reason, score and estimated value." },
  { n: 5, title: "Policy & Eligibility Engine", desc: "Licence, jurisdiction, language, channel, contact-window, restricted-claims and non-interference rules determine what is permitted for this client right now." },
  { n: 6, title: "Next-Best-Action Orchestration", desc: "Deterministic decisioning selects the action, approved language, channel, message and timing best suited to the detected pattern." },
  { n: 7, title: "Automated Action or Human Approval", desc: "Low-risk actions auto-execute under policy; high-value, KYC, withdrawal or escalated cases are routed to the correct human approver first." },
  { n: 8, title: "CRM Write-Back, Audit Log & Outcome Attribution", desc: "Outcomes are written back to the CRM, every step is recorded in an immutable audit log, and recovered revenue is attributed to the action that caused it." },
];

export const ARCH_INTEGRATIONS = [
  {
    key: "crm", name: "Broker CRM",
    read: "Lead and account records, lifecycle fields, tasks, call dispositions, owner assignments, notes and ticket states.",
    output: "Prioritisation score, next-best-action task, escalation flag and outcome code per client.",
    writeback: "Tasks, updated lifecycle stage, outcome codes, conversation summaries and attribution markers.",
    capability: "REST API (contacts, tasks, deals) + outbound webhooks for record changes.",
  },
  {
    key: "kyc", name: "KYC Provider",
    read: "Verification status, document-level results, rejection codes and reasons, retry attempts.",
    output: "Document-specific client guidance and compliance-routed approval requests.",
    writeback: "None to the provider — KYC decisions always remain with compliance; guidance is logged to the CRM.",
    capability: "Status webhooks + document-result API with machine-readable rejection codes.",
  },
  {
    key: "payments", name: "Payment System",
    read: "Deposit/withdrawal attempts, failure codes (e.g. 3DS issuer declines), settlement states, pending-review queues.",
    output: "Failure classification, approved alternative-route recommendation, withdrawal status communication.",
    writeback: "Payment-status notes and recovery outcome to the CRM; no instruction that alters the movement of funds.",
    capability: "Transaction webhooks (initiated / failed / settled) + read-only transaction API.",
  },
  {
    key: "trading", name: "Trading Platforms (MT4 / MT5 / cTrader)",
    read: "Login recency, session frequency, volume trends, open/closed positions, equity and balance snapshots.",
    output: "Activity-decline detection, dormant-risk scoring and re-engagement triggers.",
    writeback: "None — trading platforms are read-only sources; all actions route through CRM and communication channels.",
    capability: "Manager/Admin API or reporting feed with per-account activity metrics.",
  },
  {
    key: "comms", name: "Communication Channels (Email · SMS · WhatsApp · VoIP)",
    read: "Delivery, open, read and reply events; call attempts and dispositions.",
    output: "Channel selection, approved-language message execution and response detection.",
    writeback: "Sent-message records, delivery receipts and conversation summaries to the CRM timeline.",
    capability: "Send API per channel + delivery/engagement webhooks; VoIP call-log API.",
  },
];

export const EVAL_MODULES = [
  { key: "ftd", name: "FTD Module", desc: "Converts registered leads into first-time depositors: call-avoidance detection, channel switching, payment-failure recovery and multilingual human-first outreach." },
  { key: "kyc", name: "KYC Module", desc: "Recovers stalled verifications: rejection-code interpretation, document-specific client guidance and compliance-routed approvals. O.N.E.Tech never approves KYC itself." },
  { key: "activation", name: "Activation Module", desc: "Moves funded accounts to first trade and healthy early engagement: funding-nudge orchestration and onboarding-friction detection." },
  { key: "retention", name: "Retention Module", desc: "Detects activity decline before dormancy: login-gap, volume-trend and unresolved-ticket signals with save-play re-engagement." },
  { key: "withdrawal", name: "Withdrawal Coordination Module", desc: "Protects trust at the most sensitive moment: strict non-interference (WD-01), approved status communication only, and prioritised human escalation." },
];

export const EVAL_CAPABILITIES = [
  "REST API and/or outbound webhooks on CRM contact, task and deal objects",
  "Read access (API or reporting feed) to KYC results, payment events and trading activity metrics",
  "Send-capable APIs for at least one communication channel, with delivery webhooks",
  "Per-broker tenant isolation and configurable data-residency for client records",
  "Support for custom fields or objects to receive O.N.E.Tech scores, decisions and outcomes",
  "SSO / role-based access so approvals and escalations reach the right human queues",
];

export const EVAL_GOVERNANCE = [
  "Per-jurisdiction policy packs: licences, approved languages, channels, contact windows and restricted claims",
  "Human-approval thresholds configurable per broker (value, stage, escalation level)",
  "Withdrawal non-interference (WD-01) enforced at the engine level, not by convention",
  "Immutable audit log of every signal, decision, approval, execution and write-back",
  "Data sovereignty: client data remains inside the partner/broker environment; the engine processes events, not portfolios",
  "No legal commitments are made in this preview — all controls are demonstrated on synthetic data only",
];

export const EVAL_DISCOVERY = [
  "Which CRM objects and custom fields are available for write-back in your data model?",
  "Which event sources can emit webhooks today, and which require polling?",
  "What latency is acceptable between a source event and an intelligence update in the CRM UI?",
  "How are broker tenants isolated, and what data-residency constraints apply per region?",
  "Which approval roles exist in your permission model (compliance, retention, payments)?",
  "What branding hooks does your theming layer expose (name, logo, colour tokens, typography)?",
  "Which jurisdictions and licence types must the first validation cohort cover?",
  "What telemetry do you require for joint success measurement during the pilot?",
];
